export enum MessageRole {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system",
}
export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: string;
  metadata?: Record<string, any> | null;
  suggestions?: string[];
  products?: any[];
}
export interface ChatRequest {
  message: string;
  userId: string;
  sessionId?: string;
}

export interface ChatResponse {
  response: string;
  sessionId: string;
  messageId: string;
  timestamp: string;
  metadata?: Record<string, any> | null;
  suggestions?: string[];
  products?: any[];
}

export type ChatJobStatus =
  | "queued"
  | "processing"
  | "thinking"
  | "completed"
  | "failed"
  | "not_found";

export interface ChatJobInitResponse {
  jobId: string;
  status: ChatJobStatus;
  message: string;
  estimatedWaitTime?: string;
  expiresInSeconds?: number;
}

export interface ChatJobStatusResponse {
  jobId: string;
  status: ChatJobStatus;
  response: ChatResponse | null;
  error: string | null;
  queuePosition?: number;
  lastUpdatedAt?: string;
}
export interface ChatSession {
  sessionId: string;
  userId: string;
  createdAt: string;
  lastActivity: string;
  messages: ChatMessage[];
  metadata?: Record<string, any> | null;
}
export interface ChatHistoryRequest {
  userId: string;
  sessionId?: string;
  limit?: number;
}
export interface ChatHistoryResponse {
  userId: string;
  sessions: ChatSession[];
  totalSessions: number;
  totalMessages: number;
  metadata?: Record<string, any> | null;
}
export interface HealthResponse {
  status: string;
  timestamp: string;
  version: string;
  environment: string;
  services: Record<string, string>;
}
export interface ChatState {
  currentSessionId: string | null;
  messages: ChatMessage[];
  history: ChatHistoryResponse | null;
  loading: boolean;
  error: string | null;
  jobId: string | null;
  jobStatus: ChatJobStatus | null;
  queuePosition: number | null;
  lastUpdatedAt: string | null;
  estimatedWaitTime: string | null;
  jobError: string | null;
  streamingEnabled: boolean;
  isStreaming: boolean;
  streamingMessageId: string | null;
  streamingContent: string;
  progressNode: string | null;
  progressMessage: string | null;
}
export interface IRecommendation {
  id: number;
  title: string;
  description: string;
  price: number;
  image: string;
  category: string;
}
export interface IQuickAction {
  id: number;
  text: string;
  shortcut: string;
}
export interface IBackendResponse {
  type: "navigate" | "recommendations" | "initial_recommendations";
  data?: IRecommendation[] | string;
  navigateTo?: string;
}
export interface SessionDeactivationRequest {
  userId: string;
  sessionId: string;
  reason?: string;
  metadata?: Record<string, any> | null;
}
export interface SessionDeactivationResponse {
  sessionId: string;
  userId: string;
  status: string;
  deactivatedAt: string;
  reason?: string;
  message: string;
}

// WebSocket Event Types
export type WebSocketEventType = "status" | "response" | "error";

export interface WebSocketStatusEvent {
  type: "status";
  data:
    | "queued"
    | "processing"
    | "thinking"
    | { message: string; progress?: number };
  job_id?: string;
  node?: string;
  timestamp: string;
}

export interface WebSocketResponseEvent {
  type: "response";
  data: {
    status: "completed";
    response: string; // JSON string or text
    session_id: string;
    completed_at: string;
    messageId?: string;
    metadata?: Record<string, any> | null;
    suggestions?: string[];
    products?: any[];
  };
  timestamp: string;
}

export interface WebSocketErrorEvent {
  type: "error";
  data: string;
  timestamp: string;
}

export interface WebSocketStreamChunkEvent {
  type: "stream_chunk";
  data: {
    message_id: string;
    content: string;
    node?: string;
    metadata?: Record<string, any> | null;
    suggestions?: string[];
    products?: any[];
    timestamp: string;
    session_id?: string;
  };
}

export interface WebSocketStreamDoneEvent {
  type: "stream_done";
  data: {
    message_id: string;
    full_content?: string;
    text?: string;
    session_id: string;
    completed_at?: string;
    timestamp: string;
    metadata?: Record<string, any> | null;
    suggestions?: string[];
    products?: any[];
  };
}

export interface WebSocketStreamErrorEvent {
  type: "stream_error";
  message_id?: string;
  error: string;
  timestamp: string;
}

export type WebSocketEvent =
  | WebSocketStatusEvent
  | WebSocketResponseEvent
  | WebSocketErrorEvent
  | WebSocketStreamChunkEvent
  | WebSocketStreamDoneEvent
  | WebSocketStreamErrorEvent;

export interface WebSocketChatMessage {
  user_id: string;
  message: string;
  session_id?: string;
  device_fingerprint?: string;
  ip_address?: string;
  stream?: boolean;
}
